import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Driver as CLARADriver, MiniDriver } from '@uzurv/clara';
import { FileData, ServiceException, isNullOrUndefined } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Address,
  CollectionSite,
  Vehicle as DOTSVehicle,
  Document,
  DocumentTypeStatus,
  Driver,
  DriverAccountStatus,
  ImageText,
  MvrDetails,
  RandomScreeningDriverSelection,
  Screening,
  ScreeningPackageStatus,
  SignatureRequest,
  SignatureRequestDetail,
  SigningTemplateStatus,
  TransitProgram,
  TransitProgramDriver,
  Vehicle,
  WaitListDriver
} from '../../models';
import { SubstanceScreening } from '../../models/substance-screening';
import { TaxDocument } from '../../models/tax-document';
import { VerifiedAddress } from '../../models/verified-address';

@Injectable({
  providedIn: 'root'
})
export class DriverService extends UzurvRestService {
  readonly resourceUrl = 'drivers/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Finds a driver by their ID
   *
   * @param driverId
   * @permission driver
   */
  findDriverById(driverId: number): Observable<Driver> {
    return this.get(this.resourceUrl + driverId).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Update a driver
   *
   * @param driver
   */
  updateDriver(driver: Driver): Observable<Driver> {
    return this.put<Driver>(`${this.resourceUrl}${driver.id}`, Serialize(driver, Driver)).pipe(
      map(response => Deserialize(response, Driver))
    );
  }

  /**
   * Update driver address
   * @param driverId
   * @param address
   * @param deactivateOutsideOfRadius
   *
   */
  validateAndSaveAddressForDriver(
    driverId: number,
    address: Address,
    deactivateOutsideOfRadius: boolean = true
  ): Observable<VerifiedAddress> {
    return this.post<Address>(`${this.resourceUrl}${driverId}/validateAndSaveAddresses/`, Serialize(address), {
      params: { deactivateOutsideOfRadius: deactivateOutsideOfRadius.toString() }
    }).pipe(map(response => Deserialize(response, VerifiedAddress)));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param stateCode
   * @param transitProgramCodes
   * @param hasBeenApproved
   * @param searchString
   * @roles Driver Recruitment, Operations Administrator, System Administrator
   */
  listPendingDocumentReview(
    pageNumber: number,
    pageSize: number,
    stateCode?: string,
    transitProgramCodes?: string,
    hasBeenApproved?: boolean,
    searchString?: string
  ): Observable<Array<Driver>> {
    return this.get(`${this.resourceUrl}pendingDocumentReview/`, {
      transitPrograms: transitProgramCodes,
      hasBeenApproved: hasBeenApproved,
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize,
      stateCode: stateCode
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  searchPendingDocuments(searchString: string, pageNumber: number, pageSize: number): Observable<Array<Driver>> {
    return this.get(`${this.resourceUrl}pendingDocumentReview/search/`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * adds a driver to the wait list on hubspot
   * @param waitListDriver
   * @returns boolean
   */
  addDriverToWaitList(waitListDriver: WaitListDriver): Observable<boolean> {
    return this.post<boolean>(`${this.resourceUrl}joinWaitList`, waitListDriver).pipe(map(response => response));
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param stateCode
   * @param transitProgramCodes
   * @param hasBeenApproved
   * @param searchString
   * @roles Driver Recruitment, Operations Administrator, System Administrator
   */
  listPendingApproval(
    pageNumber: number,
    pageSize: number,
    stateCode?: string,
    transitProgramCodes?: string,
    hasBeenApproved?: boolean,
    searchString?: string
  ): Observable<Array<Driver>> {
    return this.get(`${this.resourceUrl}pendingApproval/`, {
      transitPrograms: transitProgramCodes,
      hasBeenApproved: hasBeenApproved,
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize,
      stateCode: stateCode
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Search a driver by arbitrary means. Will match on name, username, ID, etc.
   *
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @param stateCode
   * @permission driver_search
   */
  searchForDriver(searchString: string, stateCode: string, pageNumber = 0, pageSize = 20): Observable<Array<Driver>> {
    return this.get('search/drivers/', {
      searchString: searchString,
      stateCode: stateCode,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Gets the driver account status for a given driver. Which contains all transit program status information.
   *
   * @param dotsDriverId
   */
  getDriverAccountStatusForDriver(dotsDriverId: number): Observable<DriverAccountStatus> {
    return this.get<DriverAccountStatus>(`${this.resourceUrl}${dotsDriverId}/accountStatus/`).pipe(
      map(response => Deserialize(response, DriverAccountStatus))
    );
  }

  /**
   * Returns a list of TransitPrograms for driver
   *
   * @param dotsDriverId
   */
  getTransitProgramsByDriverId(dotsDriverId: number): Observable<Array<TransitProgramDriver>> {
    return this.get<Array<TransitProgramDriver>>(`${this.resourceUrl}${dotsDriverId}/transitPrograms/`).pipe(
      map(response => Deserialize(response, TransitProgramDriver))
    );
  }

  /**
   * Gets the profile picture file for a driver
   *
   * @param driverId
   */
  getProfilePictureForDriver(driverId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/profilePicture/`).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * Get a vehicles image as FileData
   *
   * @param vehicleId
   * @param driverId
   */
  getVehicleImageForVehicle(vehicleId: number, driverId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/vehicles/${vehicleId}/vehicleImage/`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Save the vehicle image for a vehicle as a document.
   *
   * @param id
   * @param vehicleId
   * @param vehicleImage
   */
  saveVehicleImageForDriver(id: number, vehicleId: number, vehicleImage: FileData): Observable<FileData> {
    return this.post(`${this.resourceUrl}${id}/vehicles/${vehicleId}/vehicleImage/`, Serialize(vehicleImage)).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Saves the profile picture for a driver
   *
   * @param driverId
   * @param profilePicture
   */
  saveProfilePictureForDriver(driverId: number, profilePicture: FileData): Observable<FileData> {
    return this.post(`${this.resourceUrl}${driverId}/profilePicture/`, Serialize(profilePicture)).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Applies to a transit program for driver
   *
   * @param dotsDriverId
   * @param transitProgramDriver
   */

  applyToTransitProgramForDriver(dotsDriverId: number, transitProgramDriver: TransitProgramDriver): Observable<Driver> {
    return this.post<TransitProgramDriver>(`${this.resourceUrl}${dotsDriverId}/transitPrograms/`, Serialize(transitProgramDriver)).pipe(
      map(response => Deserialize(response, Driver))
    );
  }

  /**
   * Applies to a transit program by zip code.
   *
   * @param dotsDriverId
   * @param zipCode
   */
  applyToTransitProgramForDriverByZipCode(dotsDriverId: number, zipCode: string): Observable<TransitProgramDriver> {
    return this.post<TransitProgramDriver>(`${this.resourceUrl}${dotsDriverId}/applyTransitPrograms/${zipCode}`, {}).pipe(
      map(response => Deserialize(response, TransitProgramDriver))
    );
  }

  /**
   * Deletes (unApplies) a transit program for a driver.
   *
   * @param dotsDriverId
   * @param transitProgramId
   */

  deleteTransitProgramForDriver(dotsDriverId: number, transitProgramId: number): Observable<Driver> {
    return this.delete<TransitProgramDriver>(`${this.resourceUrl}${dotsDriverId}/transitPrograms/${transitProgramId}`).pipe(
      map(response => Deserialize(response, Driver))
    );
  }

  /**
   * PATCH transitProgramDriver
   *
   * @param transitProgramDriver
   */

  patchTransitProgramDriver(transitProgramDriver: TransitProgramDriver): Observable<TransitProgram> {
    return this.patch(
      `${this.resourceUrl}${transitProgramDriver.driver.id}/transitPrograms/${transitProgramDriver.transitProgramId}`,
      Serialize(transitProgramDriver)
    ).pipe(map(response => Deserialize(response, TransitProgram)));
  }

  /**
   * Puts a driver back in the random drug screening pool
   *
   * @param dotsDriverId
   * @returns Observable<Driver>
   */
  patchRandomDrugScreeningEligibility(dotsDriverId: number): Observable<Driver> {
    return this.patch(`${this.resourceUrl}${dotsDriverId}/randomDrugScreeningEligibility/`, {
      driverId: dotsDriverId,
      randomDrugScreeningEligible: true
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Gets active documents for a driver
   *
   * @param dotsDriverId
   * @param nonReviewedOnly Pass true to only get documents that have not been reviewed
   */
  listActiveDocumentsByDriverId(dotsDriverId: number, nonReviewedOnly: boolean = false): Observable<Array<Document>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/documents/`, { nonReviewedOnly: nonReviewedOnly }).pipe(
      map(response => Deserialize(response, Document))
    );
  }

  /**
   * Gets active documents for a vehicle
   *
   * @param dotsDriverId
   * @param vehicleId
   * @param nonReviewedOnly Pass true to only get documents that have not been reviewed
   */
  listActiveVehicleDocumentsByVehicleId(
    dotsDriverId: number,
    vehicleId: number,
    nonReviewedOnly: boolean = false
  ): Observable<Array<Document>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/documents/vehicles/${vehicleId}`, {
      nonReviewedOnly: nonReviewedOnly
    }).pipe(map(response => Deserialize(response, Document)));
  }

  /**
   * Returns a list of VehicleDocumentStatuses for driver with a vehicle
   *
   * @param dotsDriverId
   * @param vehicleId
   */
  listDocumentTypeStatusesForVehicle(dotsDriverId: number, vehicleId: number): Observable<Array<DocumentTypeStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/vehicles/${vehicleId}/documentTypeStatuses/`).pipe(
      map(response => Deserialize(response, DocumentTypeStatus))
    );
  }

  /**
   * Update an array of reviewed documents.
   *
   * @param id
   * @param documents
   */
  updateReviewedDocuments(id: number, documents: Array<Document>): Observable<Array<Document>> {
    return this.put<Driver>(`${this.resourceUrl}${id}/documents/`, Serialize(documents, Document)).pipe(
      map(response => Deserialize(response, Document))
    );
  }

  /**
   * Update a document file.
   * @param id
   * @param documentId
   * @param document
   */
  updateDocumentFile(id: number, documentId: number, document: Document): Observable<Document> {
    return this.put(`${this.resourceUrl}${id}/documents/${documentId}/file/`, Serialize(document)).pipe(
      map(response => Deserialize(response, Document))
    );
  }
  /**
   *
   * @param driverId
   * @param vehicle
   */
  createVehicle(driverId: number, vehicle: Vehicle): Observable<Vehicle> {
    return this.post<Vehicle>('drivers/' + driverId + '/vehicles/', Serialize(vehicle)).pipe(
      map(response => Deserialize(response, Vehicle))
    );
  }

  /**
   * Updates an existing vehicle
   *
   * @param vehicle
   */
  updateVehicle(vehicle: Vehicle): Observable<Vehicle> {
    return this.put<Vehicle>('vehicles/' + vehicle.id, Serialize(vehicle)).pipe(map(response => Deserialize(response, Vehicle)));
  }

  /**
   * Patches an existing vehicle
   *
   * @param vehicle
   */
  patchVehicle(vehicle: Vehicle): Observable<Vehicle> {
    return this.patch<Vehicle>('vehicles/' + vehicle.id, Serialize(vehicle)).pipe(map(response => Deserialize(response, Vehicle)));
  }

  /**
   *
   * @param dotsDriverId
   */
  listVehiclesByDriverId(dotsDriverId: number): Observable<Array<Vehicle>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/vehicles/`).pipe(map(response => Deserialize(response, Vehicle)));
  }

  /**
   * List all vehicles including inactive.
   * @param driverId
   */
  listInactiveVehiclesByDriverId(driverId: number): Observable<Array<DOTSVehicle>> {
    return this.get<Array<DOTSVehicle>>(`${this.resourceUrl}${driverId}/vehicles/includeInactive/`).pipe(
      map(response => Deserialize(response, DOTSVehicle))
    );
  }

  /**
   * Returns a list of TransitProgramDocumentStatuses for driver
   *
   * @deprecated Never used. Remove for R15
   * @param dotsDriverId
   */
  listDocumentTypeStatusesForDriver(dotsDriverId: number): Observable<Array<DocumentTypeStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/documentTypeStatuses/`).pipe(
      map(response => Deserialize(response, DocumentTypeStatus))
    );
  }

  /**
   * Returns a list of TransitProgramDocumentStatuses for driver in a transit program
   *
   * @param dotsDriverId
   * @param transitProgramId
   */
  listDocumentStatusesForDriverAndTransitProgram(dotsDriverId: number, transitProgramId: number): Observable<Array<DocumentTypeStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/transitPrograms/${transitProgramId}/documentTypeStatuses/`).pipe(
      map(response => Deserialize(response, DocumentTypeStatus))
    );
  }

  /**
   * Returns a list of ScreeningPackageStatuses for driver
   *
   * @param dotsDriverId
   */
  listScreeningPackageStatusesForDriver(dotsDriverId: number): Observable<Array<ScreeningPackageStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/screeningPackageStatuses/`).pipe(
      map(response => Deserialize(response, ScreeningPackageStatus))
    );
  }

  /**
   * Returns a list of ScreeningPackageStatuses for driver in a transit program
   *
   * @param dotsDriverId
   * @param transitProgramId
   */
  listScreeningPackageStatusesForDriverAndTransitProgram(
    dotsDriverId: number,
    transitProgramId: number
  ): Observable<Array<ScreeningPackageStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/transitPrograms/${transitProgramId}/screeningPackageStatuses/`).pipe(
      map(response => Deserialize(response, ScreeningPackageStatus))
    );
  }

  /**
   * Returns a list of SigningTemplateStatuses for driver
   *
   * @param dotsDriverId
   */

  listSigningTemplateStatusesForDriver(dotsDriverId: number): Observable<Array<SigningTemplateStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/signingTemplateStatuses/`).pipe(
      map(response => Deserialize(response, SigningTemplateStatus))
    );
  }

  /**
   * Returns a list of SigningTemplateStatuses for driver in a transit program
   *
   * @param dotsDriverId
   * @param transitProgramId
   */

  listSigningTemplateStatusesForDriverAndTransitProgram(
    dotsDriverId: number,
    transitProgramId: number
  ): Observable<Array<SigningTemplateStatus>> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/transitPrograms/${transitProgramId}/signingTemplateStatuses/`).pipe(
      map(response => Deserialize(response, SigningTemplateStatus))
    );
  }

  /**
   * Resends a signature request.
   *
   * @param driverId
   * @param signatureRequestId
   * @param signatureRequestObj
   */
  resendSignatureRequest(driverId: number, signatureRequestId: number, signatureRequest: any): Observable<boolean> {
    return this.get(`${this.resourceUrl}${driverId}/signatureRequest/${signatureRequestId}`).pipe(map(response => response));
  }

  /**
   * Lists signature requests by driverId
   *
   * @param id
   */
  listSignatureRequestsByDriverId(id: number): Observable<Array<SignatureRequestDetail>> {
    return this.get(`${this.resourceUrl}${id}/signatureRequests/`).pipe(map(response => Deserialize(response, SignatureRequestDetail)));
  }

  /**
   * Gets a SignatureRequest by id
   *
   * @param signatureRequestId
   */
  getSignatureRequestById(signatureRequestId: number): Observable<SignatureRequest> {
    return this.get(`signatures/signatureRequest`, { signatureRequestId: signatureRequestId }).pipe(
      map(response => Deserialize(response, SignatureRequest))
    );
  }

  /**
   * Gets a File for given driver and SignatureRequest
   *
   * @param signatureRequestId
   * @param driverId
   */
  getFileForSignatureRequest(signatureRequestId: number, driverId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/signatureRequests/${signatureRequestId}/file`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Gets the form data for a given SignatureRequest
   *
   * @param driverId
   * @param signatureRequestId
   * @returns
   */
  getFormDataForSignatureRequest(driverId: number, signatureRequestId: number): Observable<object> {
    return this.get(`${this.resourceUrl}${driverId}/signatureRequests/${signatureRequestId}/formData/`).pipe(
      map(response => JSON.parse(response))
    );
  }

  /**
   * Saves the form data to create a signature request for a signing template
   *
   * @param driverId
   * @param signingTemplateId
   * @param formData
   * @returns
   */
  saveFormDataForSigningTemplate(driverId: number, signingTemplateId: number, formData: object): Observable<SignatureRequest> {
    return this.post<object>(
      `${this.resourceUrl}${driverId}/signingTemplates/${signingTemplateId}/signatureRequests/formData/`,
      Serialize(formData)
    ).pipe(map(response => Deserialize(response, SignatureRequest)));
  }

  /**
   * Gets a document for the signing template
   *
   * @param driverId
   * @param signingTemplateId
   */
  getSigningTemplateDocument(driverId: number, signingTemplateId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/signingTemplates/${signingTemplateId}/document/`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Update an array of signatureRequests.
   *
   * @param id
   * @param signatureRequests
   */
  updateReviewedSignatureRequests(id: number, signatureRequests: Array<SignatureRequest>): Observable<Array<SignatureRequest>> {
    return this.put<Driver>(`${this.resourceUrl}${id}/signatureRequests/`, Serialize(signatureRequests, SignatureRequest)).pipe(
      map(response => Deserialize(response, SignatureRequest))
    );
  }

  /**
   * Lists screenings by driverId
   *
   * @param id
   * @param pageNumber
   * @param pageSize
   */
  listScreeningsByDriverId(id: number, pageNumber: number, pageSize: number): Observable<Array<Screening>> {
    return this.get(`${this.resourceUrl}${id}/screenings/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Screening)));
  }

  /**
   * Creates a substance screening.
   * @param dotsDriverId
   * @param screeningPackageCode
   * @param screeningReasonCode
   */
  createSubstanceScreening(dotsDriverId: number, screeningPackageCode: string, screeningReasonCode: string): Observable<Screening> {
    return this.post<Screening>(
      `${this.resourceUrl}${dotsDriverId}/screenings/substance/?screeningPackageCode=${screeningPackageCode}&screeningReasonCode=${screeningReasonCode}`,
      {}
    ).pipe(map(response => Deserialize(response, Screening)));
  }

  /**
   * Start new criminal screening
   * @param dotsDriverId
   */
  startNewCriminalScreening(dotsDriverId: number): Observable<Screening> {
    return this.post<Screening>(`${this.resourceUrl}${dotsDriverId}/screenings/criminal/`, {}).pipe(
      map(response => Deserialize(response, Screening))
    );
  }

  /**
   * Start new Mvr screening
   * @param dotsDriverId
   */
  startNewMvrScreening(dotsDriverId: number): Observable<Screening> {
    return this.post<Screening>(`${this.resourceUrl}${dotsDriverId}/screenings/mvr/`, {}).pipe(
      map(response => Deserialize(response, Screening))
    );
  }

  /**
   * Schedules a substance screening.
   * @param dotsDriverId
   * @param screeningId
   * @param collectionSite
   */
  scheduleSubstanceScreening(dotsDriverId: number, screeningId: number, collectionSite: CollectionSite): Observable<boolean> {
    return this.post<boolean>(`${this.resourceUrl}${dotsDriverId}/screenings/${screeningId}/schedule/`, Serialize(collectionSite)).pipe(
      map(response => Deserialize(response, CollectionSite))
    );
  }

  /**
   * Deletes (cancels) a screening for a driver.
   *
   * @param dotsDriverId
   * @param screeningId
   */

  cancelSubstanceScreening(dotsDriverId: number, screeningId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}${dotsDriverId}/screenings/${screeningId}/schedule/`);
  }

  /**
   * Gets a screening by screening id
   *
   * @param dotsDriverId
   * @param screeningId
   */
  getScreeningById(dotsDriverId: number, screeningId: number): Observable<Screening> {
    return this.get(`${this.resourceUrl}${dotsDriverId}/screenings/${screeningId}`).pipe(map(response => Deserialize(response, Screening)));
  }

  /**
   * Gets a substance screening
   *
   * @param id
   * @param screeningId
   */
  getSubstanceScreeningDetails(id: number, screeningId: number): Observable<SubstanceScreening> {
    return this.get(`${this.resourceUrl}${id}/screenings/${screeningId}/substance`).pipe(
      map(response => Deserialize(response, SubstanceScreening))
    );
  }

  /**
   * Gets an MVR screening
   *
   * @param id
   * @param screeningId
   */
  getMVRScreeningDetails(id: number, screeningId: number): Observable<MvrDetails> {
    return this.get(`${this.resourceUrl}${id}/screenings/${screeningId}/mvr`).pipe(map(response => Deserialize(response, MvrDetails)));
  }

  /**
   * Gets an MVR report
   *
   * @param id
   * @param screeningId
   */
  getMVRReport(id: number, screeningId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${id}/screenings/${screeningId}/mvr/download`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  /**
   * Gets a specific document given the driver ID and the document ID
   *
   * @param documentId
   * @param driverId
   */
  findDocumentByIdForDriver(documentId: number, driverId: number): Observable<Document> {
    return this.get(`${this.resourceUrl}${driverId}/documents/${documentId}`).pipe(map(response => Deserialize(response, Document)));
  }

  /**
   * Gets a File for given driver ID and document ID
   *
   * @param documentId
   * @param driverId
   */
  getFileForDocumentIdAndDriverId(documentId: number, driverId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/documents/${documentId}/file`).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * Creates a submission of a driver document for a given driver by their ID
   *
   * @param driverId
   * @param driverDocument
   */
  saveDocumentForDriver(driverId: number, document: Document): Observable<Document> {
    return this.post<Document>(`${this.resourceUrl}${driverId}/documents/`, Serialize(document)).pipe(
      map(response => Deserialize(response, Document))
    );
  }

  /**
   * Adds an address to a driver
   *
   * @param driverId
   * @param address
   * @param deactivateOutsideOfRadius
   */
  addAddressToDriver(driverId: number, address: Address, deactivateOutsideOfRadius: boolean): Observable<Address> {
    address.driverId = driverId;
    address.id = null;
    return this.post<Address>(
      `${this.resourceUrl}${driverId}/addresses/?deactivateOutsideOfRadius=${deactivateOutsideOfRadius}`,
      Serialize(address)
    ).pipe(map(response => Deserialize(response, Address)));
  }

  /**
   * Gets a driver's current address
   *
   * @param driverId
   */
  findAddressByDriverId(driverId: number): Observable<Address> {
    if (isNullOrUndefined(driverId)) {
      return throwError(new ServiceException('Driver id Cannot Be Null'));
    }

    return this.get<Driver>(`${this.resourceUrl}${driverId}/address/`).pipe(map(response => Deserialize(response, Address)));
  }

  /**************** Signatures ************/

  listSignaturesForDriver(driverId: number): Observable<Array<Document>> {
    if (isNullOrUndefined(driverId)) {
      return throwError(new ServiceException('Driver id Cannot Be Null'));
    }

    return this.get<Driver>(`${this.resourceUrl}${driverId}/signatures/`).pipe(map(response => Deserialize(response, Document)));
  }

  saveSignatureForDriver(driverId: number, signature: FileData): Observable<Document> {
    return this.post<Document>(`${this.resourceUrl}${driverId}/signatures/`, Serialize(signature)).pipe(
      map(response => Deserialize(response, Document))
    );
  }

  /************* Screenings **************/

  getCollectionSites(driverId: number): Observable<Array<CollectionSite>> {
    return this.get<Array<CollectionSite>>(`${this.resourceUrl}${driverId}/collectionSites/${driverId}`).pipe(
      map(response => Deserialize(response, CollectionSite))
    );
  }

  getCollectionSitesByCoordinates(driverId: number, latitude: number, longitude: number): Observable<Array<CollectionSite>> {
    return this.get<Array<CollectionSite>>(`${this.resourceUrl}${driverId}/collectionSites/`, {
      latitude: latitude,
      longitude: longitude
    }).pipe(map(response => Deserialize(response, CollectionSite)));
  }

  saveScreeningInfoForDriver(driverId: number, ssn: string, dateOfBirth: Date): Observable<Driver> {
    return this.patch(`${this.resourceUrl}${driverId}`, {
      id: driverId,
      socialSecurityNumber: ssn,
      dateOfBirth: dateOfBirth
    }).pipe(map(response => Deserialize(response, Driver)));
  }

  changeSubstanceScreeningCollectionSite(dotsDriverIdentifier: number, screeningId: number): Observable<boolean> {
    return this.put(`${this.resourceUrl}${dotsDriverIdentifier}/screenings/${screeningId}/changeCollectionSite/`, null).pipe(
      map(response => response)
    );
  }

  /*********** OCR **************/

  detectTextFromImage(driverId: number, documentTypeId: number, stateId: number, image: FileData): Observable<Array<ImageText>> {
    return this.post(
      `${this.resourceUrl}${driverId}/detectTextFromImage/?documentTypeId=${documentTypeId}&stateId=${stateId}`,
      Serialize(image)
    ).pipe(map(response => Deserialize(response, ImageText)));
  }

  /************** Tax Documents ************/

  listTaxDocumentByDriverIdAndTaxYear(driverId: number, taxYear: number): Observable<Array<TaxDocument>> {
    return this.get(`${this.resourceUrl}${driverId}/taxDocuments/`, {
      taxYear: taxYear
    }).pipe(map(response => Deserialize(response, TaxDocument)));
  }

  getFileForTaxDocument(driverId: number, taxDocumentId: number): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/taxDocuments/${taxDocumentId}/file`).pipe(
      map(response => Deserialize(response, FileData))
    );
  }

  findTaxDocumentById(driverId: number, taxDocumentId: string): Observable<TaxDocument> {
    return this.get(`${this.resourceUrl}${driverId}/taxDocuments/${taxDocumentId}`).pipe(
      map(response => Deserialize(response, TaxDocument))
    );
  }

  sendTaxDocumentsToDriver(driverId: number, taxYear: number): Observable<boolean> {
    return this.post<boolean>(`${this.resourceUrl}${driverId}/taxDocuments/send?taxYear=${taxYear}`, {});
  }

  patchRandomScreeningDriverSelection(driverId: number, randomScreeningDriverSelection: RandomScreeningDriverSelection) {
    return this.patch(
      `${this.resourceUrl}${driverId}/randomScreeningDriverSelection/${randomScreeningDriverSelection.id}`,
      Serialize(randomScreeningDriverSelection)
    ).pipe(map(response => Deserialize(response, RandomScreeningDriverSelection)));
  }

  /**
   * Creates a DOTS driver from a CLARA driver
   *
   * @param claraDriver
   */
  buildDotsDriverFromClaraDriver(claraDriver: CLARADriver): Driver {
    const driver: Driver = new Driver();
    driver.claraPersonIdentifier = claraDriver.id;
    driver.id = claraDriver.dotsDriverIdentifier;
    driver.emailAddress = claraDriver.emailAddress;
    driver.firstName = claraDriver.firstName;
    driver.lastName = claraDriver.lastName;
    driver.phoneNumber = claraDriver.phoneNumber;
    // driver.active = claraDriver.active;
    driver.createdDate = claraDriver.createdDate;
    driver.modifiedDate = claraDriver.modifiedDate;
    return driver;
  }

  /**
   * Creates a DOTS driver from a CLARA driver
   *
   * @param claraDriver
   */
  buildDotsDriverFromClaraMiniDriver(miniDriver: MiniDriver): Driver {
    const driver: Driver = new Driver();
    driver.claraPersonIdentifier = miniDriver.driverId;
    driver.id = miniDriver.dotsDriverIdentifier;
    driver.emailAddress = miniDriver.emailAddress;
    driver.firstName = miniDriver.firstName;
    driver.lastName = miniDriver.lastName;
    driver.phoneNumber = miniDriver.phoneNumber;
    return driver;
  }

  protected getApiUrl(): string {
    return this.uzurvRestConfiguration.dotsApiUrl;
  }
}
